/**
 * @fileoverview Reusable MetricCard component for analytics dashboard metric display.
 * @module shared/components/ui/MetricCard
 */

import { ReactElement, ReactNode, HTMLAttributes, forwardRef } from 'react'
import { TrendingUp, TrendingDown } from 'lucide-react'
import { Card, CardContent } from './index'
import { cn } from '@/shared/lib/utils'
import { BackgroundGradientAnimation } from '@/components/ui/background-gradient-animation'
import { AnimatedCard } from './AnimatedCard'

/**
 * Trend color mapping for consistent styling across components.
 */
const trendColorMap = {
  up: 'text-positive',
  down: 'text-negative',
  flat: 'text-foreground/60',
} as const
import { MetricCardData, TrendDirection } from '@/shared/types/metrics'

/**
 * Props for the MetricCard component.
 */
interface MetricCardProps extends HTMLAttributes<HTMLDivElement> {
  /** Metric card data containing title, value, trend, and secondary metrics */
  data: MetricCardData
  /** Optional icon element to display in simple variant */
  icon?: ReactNode
  /** Visual variant: default shows trend and secondary, simple is icon+value+label */
  variant?: 'default' | 'simple'
  /** Optional class for styling the primary value (e.g., positive/negative coloring) */
  valueClassName?: string
  /** Optional custom element to render as the value (e.g., AnimatedValue) */
  valueElement?: ReactNode
  /** Optional flag to enable the background gradient animation */
  enableBackgroundAnimation?: boolean
  /** Theme for the background gradient animation */
  gradientTheme?: 'positive' | 'negative'
  /** Enable hover animation with gradient border and glow */
  enableHoverAnimation?: boolean
  /** Hover animation variant: default or subtle */
  hoverAnimationVariant?: 'default' | 'subtle'
  /** Show floating particles on hover */
  showParticles?: boolean
}

const GRADIENT_THEMES = {
  positive: {
    gradientBackgroundStart: "rgb(2, 44, 34)", // emerald-950
    gradientBackgroundEnd: "rgb(15, 23, 42)",  // slate-900
    firstColor: "6, 78, 59",                   // emerald-800
    secondColor: "30, 41, 59",                 // slate-800
    thirdColor: "5, 150, 105",                 // emerald-600
    fourthColor: "15, 23, 42",                 // slate-900
    fifthColor: "2, 44, 34",                   // emerald-950
  },
  negative: {
    gradientBackgroundStart: "rgb(40, 0, 10)", // deep rose/red
    gradientBackgroundEnd: "rgb(20, 0, 5)",    // almost black red
    firstColor: "120, 10, 40",                 // darker rose
    secondColor: "110, 15, 50",                // darker pink
    thirdColor: "100, 10, 40",                 // darker pink/rose
    fourthColor: "80, 7, 36",                  // rose-950
    fifthColor: "60, 5, 20",                   // deep red
  }
}

/**
 * Props for the TrendIndicator component.
 */
interface TrendIndicatorProps {
  /** The percentage change value */
  value: number
  /** Direction of the trend for styling */
  direction: TrendDirection
  /** Time period for comparison */
  period: string
}

/**
 * Component for displaying trend indicators with icons and colors.
 *
 * @component
 * @example
 * ```tsx
 * <TrendIndicator value={15.2} direction="up" period="vs last 30d" />
 * ```
 */
function TrendIndicator({ value, direction, period }: TrendIndicatorProps): ReactElement {
  const isPositive = direction === 'up'
  const Icon = isPositive ? TrendingUp : TrendingDown

  return (
    <div className="flex items-center">
      <Icon className={cn('w-5 h-5 mr-2', isPositive ? 'text-positive' : 'text-negative')} />
      <span className={cn('text-lg font-semibold', isPositive ? 'text-positive' : 'text-negative')}>
        {isPositive ? '+' : ''}
        {value.toFixed(1)}%
      </span>
      <span className="text-sm text-foreground/60 ml-3">{period}</span>
    </div>
  )
}

/**
 * A metric card component that displays key business metrics with trend indicators.
 * Built on existing ShadCN Card foundation with frosted glass styling.
 *
 * @component
 * @example
 * ```tsx
 * <MetricCard data={{
 *   title: "TOTAL REVENUE",
 *   value: "$425.8K",
 *   trend: { value: 15.2, direction: "up", period: "vs last 30d" },
 *   secondaryMetrics: [
 *     { label: "Net Profit", value: "$151.2K" },
 *     { label: "Margin", value: "35.5%", trend: "up" }
 *   ]
 * }} />
 * ```
 */
export const MetricCard = forwardRef<HTMLDivElement, MetricCardProps>(({
  data,
  className,
  icon,
  variant = 'default',
  valueClassName,
  valueElement,
  enableBackgroundAnimation = false,
  gradientTheme = 'positive',
  enableHoverAnimation = false,
  hoverAnimationVariant = 'subtle',
  showParticles = false,
  ...divProps
}, ref) => {
  const hasTrend = Boolean(data.trend)
  const hasSecondary = Array.isArray(data.secondaryMetrics) && data.secondaryMetrics.length > 0
  const gradientColors = GRADIENT_THEMES[gradientTheme]

  if (variant === 'simple') {
    // Split value and currency symbol for separate styling (when not using valueElement)
    const valueMatch = data.value.match(/^([+\-]?[\d.,]+[KMB]?)\s*(.*)$/)
    const numericPart = valueMatch?.[1] ?? data.value
    const currencyPart = valueMatch?.[2] ?? ''

    const cardContent = (
      <Card ref={ref} className={cn('glass-card relative overflow-hidden', className)} {...divProps}>
        {enableBackgroundAnimation && (
          <BackgroundGradientAnimation
            {...gradientColors}
            containerClassName="absolute inset-0 opacity-50 pointer-events-none"
            interactive={false}
            size="120%"
          />
        )}
        <CardContent className="pl-3 pr-3 py-2 relative z-10">
          <div className="flex flex-col">
            <p className="whitespace-nowrap text-center mb-1">
              {valueElement ? (
                <span
                  className={cn(
                    'text-3xl md:text-4xl font-semibold tracking-tight font-mono text-foreground',
                    enableHoverAnimation && 'animated-card-value',
                    valueClassName
                  )}
                >
                  {valueElement}
                </span>
              ) : (
                <>
                  <span
                    className={cn(
                      'text-3xl md:text-4xl font-semibold tracking-tight font-mono text-foreground',
                      enableHoverAnimation && 'animated-card-value',
                      valueClassName
                    )}
                  >
                    {numericPart}
                  </span>
                  {currencyPart ? (
                    <span
                      className={cn(
                        'text-2xl md:text-3xl font-medium text-muted-foreground ml-2',
                        valueClassName
                      )}
                    >
                      {currencyPart}
                    </span>
                  ) : null}
                </>
              )}
            </p>
            <div className="flex justify-center">
              <p className="mt-0 text-base text-muted-foreground inline-flex items-center">
                {icon ? (
                  <span
                    aria-hidden
                    className="mr-2 inline-flex items-center text-muted-foreground align-middle"
                  >
                    {icon}
                  </span>
                ) : null}
                {data.title}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    )

    if (enableHoverAnimation) {
      return (
        <AnimatedCard variant={hoverAnimationVariant} showParticles={showParticles}>
          {cardContent}
        </AnimatedCard>
      )
    }

    return cardContent
  }

  const cardContent = (
    <Card ref={ref} className={cn('glass-card relative overflow-hidden', className)} {...divProps}>
      {enableBackgroundAnimation && (
        <BackgroundGradientAnimation
          {...gradientColors}
          containerClassName="absolute inset-0 opacity-20"
          interactive={false}
          size="120%"
        />
      )}
      <CardContent className="p-0 relative z-10">
        {/* Primary Metric Section */}
        <div className="mb-6">
          <p className="text-sm font-medium uppercase tracking-wide text-foreground/80 mb-2">
            {data.title}
          </p>
          <p className="text-6xl font-bold text-foreground font-mono mb-4 leading-[1.1]">
            {data.value}
          </p>
          {hasTrend && data.trend ? (
            <TrendIndicator
              value={data.trend.value}
              direction={data.trend.direction}
              period={data.trend.period}
            />
          ) : null}
        </div>

        {hasSecondary ? (
          <div className="border-t border-foreground/20 pt-6">
            <div className="flex justify-between items-center">
              {data.secondaryMetrics!.map((metric, index) => (
                <div key={index} className={index === 1 ? 'text-right' : ''}>
                  <p className="text-sm font-medium uppercase tracking-wide text-foreground/80">
                    {metric.label}
                  </p>
                  <p
                    className={cn(
                      'text-lg font-mono',
                      trendColorMap[metric.trend as keyof typeof trendColorMap] ||
                        trendColorMap.flat
                    )}
                  >
                    {metric.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )

  if (enableHoverAnimation) {
    return (
      <AnimatedCard variant={hoverAnimationVariant} showParticles={showParticles}>
        {cardContent}
      </AnimatedCard>
    )
  }

  return cardContent
})
MetricCard.displayName = "MetricCard"
